import React, { useEffect } from 'react';
import { useSelectInput, SelectOption } from './hooks/useSelectInput';
import { catalogClient } from '../services/grpc';
import { ModReq } from '../generated/catalog_pb';
import '../styles/select-input.css';

const loadModifications = async (
  query: string,
  modelId: number,
  year: number
): Promise<SelectOption[]> => {
  const req = new ModReq();
  req.setName(query);
  req.setModel(modelId);
  req.setYear(year);

  return new Promise((resolve, reject) => {
    catalogClient.modRequest(req, {}, (err, response) => {
      if (err) {
        reject(err);
        return;
      }

      const items =
        response?.getListList().map((item) => ({
          id: item.getModId(),
          label: item.getName(),
        })) || [];

      resolve(items);
    });
  });
};

interface ModSelectProps {
  modelId: number | null;
  year: number | null;
  onModSelect?: (mod: SelectOption | null) => void;
  inputId?: string;
  placeholder?: string;
  disabled?: boolean;
  disabledPlaceholder?: string;
}

const ModSelect: React.FC<ModSelectProps> = ({
  modelId,
  year,
  onModSelect,
  inputId,
  placeholder = 'Выберите модификацию...',
  disabled,
  disabledPlaceholder = 'Сначала выберите модель и год',
}) => {
  const isDisabled = disabled || !modelId || !year;

  const {
    inputValue,
    setInputValue,
    selectedOption,
    options,
    isLoading,
    isOpen,
    handleSelect,
    handleBlur,
    openDropdown,
    reset,
    loadCurrentOptions,
  } = useSelectInput<SelectOption>({
    loadOptions: (query) => {
      if (!modelId || !year) {
        return Promise.resolve([]);
      }

      return loadModifications(query, modelId, year);
    },
    debounceMs: 300,
    minQueryLength: 0,
  });

  useEffect(() => {
    if (onModSelect) {
      onModSelect(selectedOption);
    }
  }, [selectedOption, onModSelect]);

  useEffect(() => {
    reset();
  }, [modelId, year, reset]);

  useEffect(() => {
    if (isDisabled) {
      reset();
    }
  }, [isDisabled, reset]);

  const placeholderText = isDisabled ? disabledPlaceholder : placeholder;
  const shouldOpenOnFocus = !isDisabled && options.length > 0;

  return (
    <div className="select-input" onBlur={handleBlur}>
      <input
        id={inputId}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder={placeholderText}
        onFocus={() => {
          if (isDisabled) {
            return;
          }

          if (inputValue === '') {
            if (options.length === 0) {
              loadCurrentOptions();
            } else {
              openDropdown();
            }
          } else if (shouldOpenOnFocus) {
            openDropdown();
          }
        }}
        className="select-input__field"
        autoComplete="off"
        disabled={isDisabled}
      />

      {isOpen && !isDisabled && isLoading && (
        <div className="select-input__status select-input__status--loading">
          Загрузка...
        </div>
      )}

      {isOpen && !isDisabled && !isLoading && options.length > 0 && (
        <ul className="select-input__dropdown">
          {options.map((option) => (
            <li
              key={option.id}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelect(option)}
              className="select-input__option"
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}

      {isOpen && !isDisabled && !isLoading && options.length === 0 && (
        <div className="select-input__status">Ничего не найдено</div>
      )}
    </div>
  );
};

export default ModSelect;
